<template>
  <div>
    <editable-map
      editable
      ref="theMap"
      id="theMap"
      :zoom.sync="currentZoom"
      :options="mapOptions"
      :center="center"
      :min-zoom="minZoom"
      :max-zoom="maxZoom"
      :bounds.sync="bounds"
      style="height: 100%; width: 100%"
      @ready="emitReady"
    >
      <l-tile-layer
        v-for="tileProvider in tileProviders"
        :key="tileProvider.name"
        :name="tileProvider.name"
        :visible="tileProvider.visible"
        :url="tileProvider.url"
        :attribution="tileProvider.attribution"
        layer-type="base"
        :options="tileProvider.options"
        v-if="tileProvidersReady"
      />
      <l-control-fullscreen position="topleft"
        :options="{title: { false: $gettext('Go fullscreen'), true: $gettext('Back to normal') }}"
      />
      <l-control-layers
        :position="layersPosition"
        :collapsed="false"
        :sort-layers="true"
      />
      <l-control-zoom :position="zoomPosition" />
      <l-control-attribution
        :position="attributionPosition"
        prefix="Moova"
      />
      <l-control-scale position="bottomright" :imperial="imperial" />
      <l-control position="topright" >
        <b-button size="sm" @click="fitMap" v-translate>
          Fit map
        </b-button>
        <div v-on:mouseover="disableMapInteraction" v-on:mouseout="enableMapInteraction" style="margin-top: 10px; box-shadow: none; height: 100%; max-height: calc(80vh - 180px); overflow: auto">
          <slot name="controls-topright"></slot>
        </div>
      </l-control>
      <l-control position="topleft" v-if="polygonable">
        <div class="leaflet-bar">
          <a href="#" @click.prevent="paths.length === 0 ? addPath() : removePath();" style="position: relative;">▱<span v-if="paths.length !== 0" style="position: absolute;left: 50%; transform: translateX(-50%);font-size: 1.5rem" class="text-danger">×</span></a>
        </div>
      </l-control>
      <l-control position="topleft" v-if="polygonable">
        <b-button size="sm" @click="multiRemoveFromCollection" :disabled="this.inPolygon.length === 0" style="padding: .2rem .4rem" v-translate>Delete from Collection</b-button>
      </l-control>

      <l-control position="bottomleft">
        <div v-on:mouseover="disableMapInteraction" v-on:mouseout="enableMapInteraction">
          <slot name="controls-bottomleft"></slot>
        </div>
      </l-control>
      <l-marker
        v-for="marker in markers"
        :key="marker.id"
        :lat-lng="[marker.position.lat, marker.position.lng]"
        :visible="true"
        :draggable="false"
      >
        <l-icon
          :icon-anchor="marker.icon.iconAnchor"
          style="position: relative;"
          :tooltipAnchor="marker.icon.tooltipAnchor"
          :popupAnchor="marker.icon.popupAnchor"
          :icon-size="marker.icon.iconSize"
        >
          <div style="position: relative; width: 100%; height: 100%;">
            <img :src="marker.icon.iconUrl" v-if="marker.icon.iconUrl" alt="Pin">
            <div v-else-if="marker.icon.iconType === 'marker-circle'" class="marker-circle" :style="'width: ' + marker.icon.iconSize[1] + 'px; height: ' + marker.icon.iconSize[1] + 'px; border-color: ' + marker.icon.iconColor">
              <span>{{marker.icon.iconText}}</span>
            </div>
            <span v-else>
              <span :style="'font-size: ' + marker.icon.iconSize[1] + 'px; line-height: 1'">
                <font-awesome-icon  size="1x" :icon="marker.icon.iconType" style="filter:drop-shadow(0 0 2px rgba(0,0,0,.4));" :color="marker.icon.iconColor" />
              </span>
              <span class="pin-text text-white" :style="textContrastColor(marker.icon.iconColor.replace('#', '')) + '!important'">{{marker.icon.iconText}}</span>
            </span>
          </div>
        </l-icon>
        <l-popup v-if="marker.popup">

          <div v-if="marker.infoText.type === 'courier'">
            <user-card :user='marker.infoText.user' :withMap="false" :profilePicture="false" :withTitles="false"></user-card>
            <b-button size="sm" @click="emitCourier(marker.infoText.id)" v-if="marker.infoText.withPanel" v-translate>
              Open Info Panel
            </b-button>
          </div>

          <div v-if="marker.infoText.type === 'shipping'">
            <p v-if="marker.infoText.ids"><br>
              ID/s: <strong v-for="(id, index) in marker.infoText.ids" :key="index">
                <a :href="'/edit/shippings/' + id" target="_blank">{{ id }}</a>
                <a href="#" @click.prevent="copyToClipboardFromMap(id)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a><span v-if="(index + 1) < marker.infoText.ids.length">, </span>
              </strong>
            </p>
            <div v-html="marker.infoText.html"></div>
          </div>

          <div v-if="marker.infoText.type === 'food-courier'">
            <p v-if="marker.infoText.ids"><br>
              ID: <strong v-for="(id, index) in marker.infoText.ids" :key="index">
                {{ id }}
                <a href="#" @click.prevent="copyToClipboardFromMap(id)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a><span v-if="(index + 1) < marker.infoText.ids.length">, </span>
              </strong>
            </p>
            <div v-html="marker.infoText.html"></div>
            <div class="text-censter p-1">
              <h6 v-translate>Assign Shipping</h6>
              <b-form @submit.prevent="emitAssignShipping(marker.infoText.id)">
                <b-input-group left="Username">
                  <b-form-input v-model="shipping.id"
                    type="text"
                    placeholder="Shipping ID"
                    required
                    :state="isValidUuid"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button size="sm" variant="primary" type="submit" :disabled="!isValidUuid" v-translate>
                      Assign!
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form>
            </div>
          </div>

          <div v-if="marker.infoText.type === 'food-shipping'">
            <p v-if="marker.infoText.ids"><br>
              ID/s: <strong v-for="(id, index) in marker.infoText.ids" :key="index">
                {{ id }}
                <a href="#" @click.prevent="copyToClipboardFromMap(id)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a><span v-if="(index + 1) < marker.infoText.ids.length">, </span>
              </strong>
            </p>
            <div v-html="marker.infoText.html"></div>
            <div class="p-1">
              <h6 v-translate>Assign to Courier</h6>
              <b-form @submit.prevent="emitAssignShipping(null, marker.infoText.id)">
                <b-input-group left="Username">
                  <b-form-input v-model="courier.id"
                    type="text"
                    placeholder="Courier ID"
                    required
                    :state="isValidInteger"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button size="sm" variant="primary" type="submit" :disabled="!isValidInteger" v-translate>
                      Assign!
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form>
            </div>
          </div>

          <div v-if="marker.infoText.type === 'collection'">
            <div v-html="marker.infoText.html"></div>
            <b-button variant="danger" size="sm" @click="emitRemoveFromCollection(marker.infoText.id); infoWinOpen = false" v-translate>
              Remove from Collection
            </b-button>
          </div>

          <div v-if="marker.infoText.type === 'cluster'">
            <div v-html="marker.infoText.html"></div>
            <div class="text-center">
              <form class="mb-2" @submit.prevent.stop="emitMoveToCluster(marker.infoText.id, clusterTo); infoWinOpen = false">
                <translate>Send to Cluster:</translate>
                <b-form-select v-model="clusterTo" required class="w-auto">
                  <option v-for="(cluster, index) in clusters" :key="index" :value="cluster - 1" :disabled="cluster - 1 === marker.infoText.cluster">
                    {{ cluster }}
                  </option>
                </b-form-select>
                <b-button variant="secondary" size="sm" type="submit" v-translate>
                  Move!
                </b-button>
              </form>
              <b-button variant="primary" size="sm" @click="emitSelectCluster(marker.infoText.cluster); infoWinOpen = false" class="mb-2" v-translate>
                Select this Cluster
              </b-button><br>
              <b-button variant="danger" size="sm" @click="emitRemoveFromCollection(marker.infoText.id); infoWinOpen = false" v-translate>
                Remove from Collection
              </b-button>
            </div>
          </div>

          <div v-if="marker.infoText.type === 'megaRoutingCluster'">
            <div v-html="marker.infoText.html"></div>
            <div class="text-center">
              <b-button variant="danger" size="sm" @click="emitRemoveFromCollection(marker.infoText.id); infoWinOpen = false" v-translate>
                Remove from Group
              </b-button>
            </div>
          </div>

          <div v-if="marker.infoText.type === 'visit'">
            <p v-if="marker.infoText.ids"><br>
              ID/s: <strong v-for="(id, index) in marker.infoText.ids" :key="index">
                {{ id }}
                <a href="#" @click.prevent="copyToClipboardFromMap(id)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a><span v-if="(index + 1) < marker.infoText.ids.length">, </span>
              </strong>
            </p>
            <div v-html="marker.infoText.html"></div>
            <b-button v-show="marker.infoText.visited !== true && !marker.infoText.multi" variant="danger" v-b-modal.modalDeleteFromTour size="sm" @click.stop.prevent="$parent.toDelete.id = marker.infoText.id; infoWinOpen = false" v-translate>DELETE from this Tour</b-button>
          </div>

        </l-popup>
        <l-tooltip :content="marker.infoText" v-if="marker.tooltip" />
      </l-marker>
      <l-polyline
        v-for="item in polylines"
        :key="item.id"
        :lat-lngs="item.points"
        :visible="item.visible || true"
        :color="item.strokeColor"
        :weight="item.strokeWeight"
      />
      <editable-polygon
        ref="polygon"
        :editable="editablePolygon"
        :lat-lngs="paths"
        @editable:editing="updateEdited($event)"
      />
      <editable-polygon
        ref="areas"
        v-if="showAreas"
        :editable="false"
        :lat-lngs="item.value.coords"
        v-for="(item, index) in areas"
        :key="'areas' + index"
      >
        <l-tooltip v-if="showAreasTooltips" :content="item.text" :options="{className: 'leaflet-tooltip-areas', permanent: true, direction: 'center'}" />
      </editable-polygon>
      <l-hotline v-if="heathPath.length > 0" :latLngs="heathPath" :min="50" :max="0"></l-hotline>
    </editable-map>
  </div>
</template>

<script>
import { latLngBounds } from 'leaflet'
import {
  LMap,
  LTileLayer,
  LMarker,
  LPolyline,
  LLayerGroup,
  LTooltip,
  LPopup,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LControlLayers,
  LControl,
  LIcon
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { EditableMap, EditablePolygon } from 'vue2-leaflet-editable'
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import LHotline from '@/components/Vue2LeafletHotline.vue'
import LControlFullscreen from 'vue2-leaflet-fullscreen'

export default {
  name: 'LeafletMap',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolyline,
    LLayerGroup,
    LTooltip,
    LPopup,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LControlLayers,
    LControl,
    LIcon,
    EditableMap,
    EditablePolygon,
    LHotline,
    LControlFullscreen
  },
  data() {
    return {
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      currentZoom: 11,
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      markers: [],
      previousMarkers: null,
      pendingMarkers: null,
      pendingPolylines: null,
      polylines: [],
      clusters: [],
      clusterTo: null,
      paths: [],
      editablePolygon: false,
      inPolygon: [],
      bounds: null,
      fitted: false,
      shipping: {},
      courier: {},
      heathPath: [],
      areas: [],
      showAreas: false,
      showAreasTooltips: false
    }
  },
  props: {
    geolocate: {
      type: Boolean,
      default: false
    },
    fitOnce: {
      type: Boolean,
      default: false
    },
    fitAlways: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: 11
    },
    centerMap: {
      type: Boolean,
      default: false
    },
    polygonable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isValidUuid() {
      if (this.shipping.id) {
        const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        return pattern.test(this.shipping.id)
      } else {
        return null
      }
    },
    isValidInteger() {
      if (this.courier.id) {
        return !isNaN(this.courier.id) && (function (x) { return (x | 0) === x })(parseFloat(this.courier.id))
      } else {
        return null
      }
    }
  },
  watch: {
    markers(val) {
      this.clusters = [...new Set(val.map(x => { return x.infoText.cluster + 1 }).filter(Boolean))]
    }
  },
  created() {
    this.checkMapProviders()
  },
  mounted() {
    if (this.geolocate === true) {
      this.geolocateMap()
    }
  },
  methods: {
    alert(item) {
      alert('this is ' + JSON.stringify(item))
    },
    geolocateMap() {
      navigator.geolocation.getCurrentPosition(position => {
        this.center = [position.coords.latitude, position.coords.longitude]
      })
    },
    addHeathPath(path) {
      this.heathPath = path
    },
    removeHeathPath() {
      this.heathPath = []
    },
    addMarkers(items) {
      if (!this.mapReady) {
        // The map is not initialized yet: keep the items and let emitReady() draw them
        this.pendingMarkers = items
        return
      }
      if (this.previousMarkers !== JSON.stringify(items)) {
        this.polylines = []
        this.markers = []
        this.markers = items.map(x => {
          return {
            id: x.id,
            position: {
              lat: x.lat,
              lng: x.lng
            },
            icon: typeof x.icon === 'string' ? {
              iconUrl: x.icon,
              iconAnchor: x.anchor || [x.x, x.y],
              iconSize: x.size || [x.x * 2, x.y * 2],
              tooltipAnchor: x.tooltipAnchor || [x.x, 0],
              popupAnchor: x.popupAnchor || [0, -x.y]
            } : {
              iconType: x.icon.type,
              iconAnchor: x.icon.anchor,
              iconSize: x.icon.size,
              tooltipAnchor: x.icon.tooltipAnchor || [x.x, 0],
              popupAnchor: x.icon.popupAnchor || [0, -x.y],
              iconColor: x.icon.color,
              iconText: x.icon.text
            },
            infoText: x.infoText,
            tooltip: x.tooltip || null,
            popup: x.popup || null
          }
        })
        this.previousMarkers = JSON.stringify(items)
        if ((this.fitOnce && this.fitted === false) || this.fitAlways) {
          this.fitMap()
          this.map.invalidateSize()
        }
      }
    },
    addPolylines(items) {
      if (!this.mapReady) {
        // Keep the pair together: emitReady() draws these right after the pending markers
        this.pendingPolylines = items
        return
      }
      this.polylines = []
      this.polylines = items.map((x, i) => {
        return {
          id: 'p' + i,
          strokeWeight: x.polyLineOptions.strokeWeight || 1,
          strokeColor: x.polyLineOptions.strokeColor,
          points: x.polyLine.filter(x => x.id !== 1000)
        }
      })
    },
    addPairs(items) {
      const markers = []
      const polyLines = []

      for (const item of items) {
        markers.push(item.from)
        markers.push(item.to)

        if (item.polyLine === true) {
          polyLines.push({
            polyLine: [
              {
                lat: parseFloat(item.from.lat),
                lng: parseFloat(item.from.lng)
              },
              {
                lat: parseFloat(item.to.lat),
                lng: parseFloat(item.to.lng)
              }
            ],
            polyLineOptions: {
              strokeColor: '#FF6C87',
              strokeWeight: 1
            }
          })
        }
      }
      this.addMarkers(markers)
      this.addPolylines(polyLines)
    },
    fitMap() {
      if (this.markers.length > 1) {
        const bounds = latLngBounds(this.markers.map(o => o.position))
        this.bounds = bounds
        this.fitted = true
      } else {
        setTimeout(function () {
          this.map.invalidateSize()
          this.center = this.markers[0] ? this.markers[0].position : this.center
        }.bind(this), 100)
      }
    },
    addPath () {
      this.noFreehand = true
      // obtain the bounds, so we can guess how big the polygon should be
      const bounds = this.map.getBounds()
      const northEast = bounds.getNorthEast()
      const southWest = bounds.getSouthWest()
      const center = bounds.getCenter()
      const degree = this.paths.length + 1
      const f = Math.pow(0.66, degree)

      // Draw a triangle. Use f to control the size of the triangle.
      // i.e., every time we add a path, we reduce the size of the triangle
      const path = [
        { lng: center.lng, lat: (1 - f) * center.lat + (f) * northEast.lat },
        { lng: (1 - f) * center.lng + (f) * southWest.lng, lat: (1 - f) * center.lat + (f) * southWest.lat },
        { lng: (1 - f) * center.lng + (f) * northEast.lng, lat: (1 - f) * center.lat + (f) * southWest.lat }
      ]

      this.paths.push(path)
      this.$nextTick(() => {
        this.editablePolygon = true
      })
      this.updateInPolygon()
    },
    removePath() {
      this.editablePolygon = false
      this.paths = []
      this.inPolygon = []
    },
    updateInPolygon() {
      this.inPolygon = []
      const polys = {
        type: 'Polygon',
        coordinates: this.paths.map(path => this.closeLoop(path.map(({lat, lng}) => [lng, lat])))
      }

      this.markers.forEach((marker, i) => {
        const point = {
          type: 'Point',
          coordinates: [marker.position.lng, marker.position.lat]
        }
        if (booleanPointInPolygon(point, polys)) {
          this.inPolygon.push(this.markers[i].infoText.id)
        }
      })
    },
    updateEdited(e) {
      const latLngs = e.layer.getLatLngs()[0]
      this.paths[0] = latLngs.map(ll => { return {lat: ll.lat, lng: ll.lng} })
      this.updateInPolygon()
    },
    closeLoop (path) {
      return path.concat(path.slice(0, 1))
    },
    multiRemoveFromCollection() {
      this.$emit('itemsRemoved', this.inPolygon)
      this.removePath()
    },
    emitReady() {
      this.map = this.$refs.theMap.mapObject
      this.map.invalidateSize()
      this.map.on('popupclose', () => this.emitCourier(null))
      this.mapReady = true
      this.drawPending()
      this.$emit('ready', true)
    },
    // Draws whatever was requested before the map was ready. Markers first: their
    // draw resets polylines, so replaying them in the other order would wipe a pair
    drawPending() {
      if (this.pendingMarkers) {
        const items = this.pendingMarkers
        this.pendingMarkers = null
        this.addMarkers(items)
      }
      if (this.pendingPolylines) {
        const items = this.pendingPolylines
        this.pendingPolylines = null
        this.addPolylines(items)
      }
    },
    emitCourier(courier) {
      this.$emit('courier-selected', courier)
    },
    emitRemoveFromCollection(i) {
      this.$emit('itemRemoved', i)
    },
    emitSelectCluster(i) {
      this.$emit('clusterSelected', i)
    },
    emitMoveToCluster(id, cluster) {
      this.$emit('moveToCluster', {id, cluster})
      this.clusterTo = null
    },
    emitAssignShipping(courierId = null, shippingId = null) {
      shippingId = shippingId || this.shipping.id
      courierId = courierId || this.courier.id
      this.$emit('assign-shipping', {courierId, shippingId})
      this.shipping = {}
      this.courier = {}
      this.infoWinOpen = false
    },
    copyToClipboardFromMap(str, type = 'id') { // Duplicated from mixins using getElementById, otherwise it fails
      const el = document.createElement('textarea')
      el.value = str
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.getElementById('theMap').appendChild(el)
      el.select()
      try {
        document.execCommand('copy')
        let mssg
        switch (type) {
          case 'id':
            mssg = this.$gettext('The ID has been copied to Clipboard')
            break
          case 'email':
            mssg = this.$gettext('The e-mail has been copied to Clipboard')
            break
          default:
            mssg = this.$gettextInterpolate(this.$gettext('The %{type} has been copied to Clipboard'), {type})
        }
        this.makeToast(this.$gettext('Notice'), mssg, 'success', false, 2000)
      } catch (e) {
        let mssg
        switch (type) {
          case 'id':
            mssg = this.$gettext('The ID could NOT be copied to Clipboard')
            break
          case 'email':
            mssg = this.$gettext('The e-mail could NOT be copied to Clipboard')
            break
          default:
            mssg = this.$gettextInterpolate(this.$gettext('The %{type} could NOT be copied to Clipboard'), {type})
        }
        this.makeToast(this.$gettext('Warning'), mssg, 'danger', false, 2000)
      }
      document.getElementById('theMap').removeChild(el)
    },
    disableMapInteraction() {
      this.map.dragging.disable()
      this.map.touchZoom.disable()
      this.map.doubleClickZoom.disable()
      this.map.scrollWheelZoom.disable()
      this.map.boxZoom.disable()
      this.map.keyboard.disable()
      if (this.map.tap) this.map.tap.disable()
    },
    enableMapInteraction() {
      this.map.dragging.enable()
      this.map.touchZoom.enable()
      this.map.doubleClickZoom.enable()
      this.map.scrollWheelZoom.enable()
      this.map.boxZoom.enable()
      this.map.keyboard.enable()
      if (this.map.tap) this.map.tap.enable()
    }
  }
}
</script>

<style lang="scss">
.leaflet-container {
  font-family: inherit !important;
}
.leaflet-popup-content p {
  margin: 0 0 1rem !important;
}
.pin-text {
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
}
.marker-circle {
  border-radius: 100px;
  border: 2px solid #000;
  background: #fff;
  text-align: center;
  span {
    font-size: 8px;
    line-height: 8px;
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    text-shadow: 0px 0px 2px rgba(255, 255, 255, 1);
  }
}
</style>
